<template>
  <div class="lesson-container">
    <div class="container">
      <div class="header">
        <h1>{{ planTitle }}</h1>
        <p v-if="planIndicator">
          <strong>Indicadores:</strong> {{ planIndicator }}
        </p>
      </div>
      <table class="planning-table">
        <!-- INICIO -->
        <tr>
          <td class="section-header inicio-header">INICIO</td>
        </tr>
        <tr v-if="inicioPregunta">
          <td class="content-cell inicio">
            <div class="subsection-title">Pregunta</div>
            <p v-html="inicioPregunta"></p>
          </td>
        </tr>
        <tr v-if="inicioBajada">
          <td class="content-cell inicio">
            <div class="subsection-title">Bajada de pregunta</div>
            <p v-html="inicioBajada"></p>
          </td>
        </tr>
        <tr v-if="inicioIntro">
          <td class="content-cell inicio">
            <div class="subsection-title">Introducción del objetivo</div>
            <p v-html="inicioIntro"></p>
          </td>
        </tr>

        <!-- DESARROLLO -->
        <tr>
          <td class="section-header desarrollo-header">DESARROLLO</td>
        </tr>
        <tr v-if="desarrolloExplicacion.length">
          <td class="content-cell desarrollo">
            <div class="subsection-title">Explicación</div>
            <div v-if="desarrolloExplicacion.length > 0">
              <div v-for="(section, index) in desarrolloExplicacion" :key="index" class="explanation-section">
                <h4 v-html="formatRichText(section.title)"></h4>
                <p v-html="formatRichText(section.text)"></p>
              </div>
            </div>
            <p v-else>No hay explicación disponible.</p>
          </td>
        </tr>
        <tr v-if="desarrolloPasoAPaso.length">
          <td class="content-cell desarrollo">
            <div class="subsection-title">
              Entrégale un paso a paso como forma de automonitoreo a los
              estudiantes
            </div>
            <ul v-if="desarrolloPasoAPaso.length > 0">
              <li
                v-for="(paso, index) in desarrolloPasoAPaso"
                :key="index"
                v-html="paso"
              ></li>
            </ul>
            <p v-else>No hay pasos definidos.</p>
          </td>
        </tr>
        <tr v-if="desarrolloErrores.length">
          <td class="content-cell desarrollo">
            <div class="subsection-title">
              Avísale a los estudiantes cuáles son los errores comunes para que
              se anticipen
            </div>
            <div v-if="desarrolloErrores.length > 0">
              <div v-for="(error, index) in desarrolloErrores" :key="index" class="error-card">
                <h4>{{ error.title }}</h4>
                <p><strong>¿Por qué ocurre?</strong> <span v-html="formatRichText(error.why)"></span></p>
                <p><strong>Ejemplo:</strong> <span v-html="formatRichText(error.example)"></span></p>
                <p><strong>Estrategia:</strong> <span v-html="formatRichText(error.strategy)"></span></p>
              </div>
            </div>
            <p v-else>No hay errores definidos.</p>
          </td>
        </tr>
        <tr v-if="desarrolloConceptos.length">
          <td class="content-cell desarrollo">
            <div class="subsection-title">
              Define y entrega los conceptos importantes antes de empezar la
              clase
            </div>
            <div v-if="desarrolloConceptos.length > 0">
              <div v-for="(concept, index) in desarrolloConceptos" :key="index" class="concept-card">
                <h4 :class="{ 'bold-term': concept.bold }">{{ concept.term }}</h4>
                <p><strong>¿Qué es?</strong> <span v-html="formatRichText(concept.what)"></span></p>
                <p><strong>¿Cómo identificarlo?</strong> <span v-html="formatRichText(concept.how_to_spot)"></span></p>
                <p><strong>¿Por qué es importante?</strong> <span v-html="formatRichText(concept.why_it_matters)"></span></p>
              </div>
            </div>
            <p v-else>No hay conceptos definidos.</p>
          </td>
        </tr>
        <tr v-if="desarrolloEjemplo">
          <td class="content-cell desarrollo">
            <div class="subsection-title">
              Aterriza el contenido a un ejemplo cercano
            </div>
            <div v-if="desarrolloEjemplo" class="worked-example-card">
              <h4>Caso trabajado en conjunto</h4>
              <p v-html="formatRichText(desarrolloEjemplo.case)"></p>
              <div v-if="desarrolloEjemplo.bullets && desarrolloEjemplo.bullets.length > 0">
                <h5>Puntos clave:</h5>
                <ul>
                  <li
                    v-for="(bullet, index) in desarrolloEjemplo.bullets"
                    :key="index"
                    v-html="formatRichText(bullet)"
                  ></li>
                </ul>
              </div>
              <div>
                <h4>Análisis del caso</h4>
                <ul>
                  <li
                    v-for="(element, index) in desarrolloEjemplo.analysis"
                    :key="index"
                    v-html="formatRichText(element)"
                  ></li>
                </ul>
              </div>
              <div>
                <h4>Estrategia</h4>
                <ul>
                  <li
                    v-for="(element, index) in desarrolloEjemplo.strategy"
                    :key="index"
                    v-html="formatRichText(element)"
                  ></li>
                </ul>
              </div>
              <div>
                <h4>Pasos de resolución</h4>
                <ul>
                  <li
                    v-for="(element, index) in desarrolloEjemplo.resolution_steps"
                    :key="index"
                    v-html="formatRichText(element)"
                  ></li>
                </ul>
              </div>
              <div>
                <h4>Respuesta</h4>
                <p v-html="formatRichText(desarrolloEjemplo.answer_model)"></p>
              </div>
            </div>
            <p v-else>No hay ejemplo disponible.</p>
          </td>
        </tr>

        <!-- EJERCICIOS BLOOM -->
        <tr v-for="(ejercicio, index) in ejerciciosBloom" :key="index">
          <td class="content-cell desarrollo">
            <div class="subsection-title">{{ ejercicio.titulo }}</div>
            <div class="exercise-card">
              <div class="exercise-level">{{ ejercicio.nivel }}</div>
              <div class="exercise-content">
                <p v-html="ejercicio.escenario"></p>
                <p v-html="ejercicio.pregunta"></p>
                <div
                  v-if="ejercicio.opciones"
                  class="exercise-options"
                  v-html="ejercicio.opciones"
                ></div>
                <div
                  v-if="ejercicio.respuesta"
                  class="exercise-answer"
                  v-html="ejercicio.respuesta"
                ></div>
              </div>
            </div>
          </td>
        </tr>

        <!-- CIERRE -->
        <tr>
          <td class="section-header cierre-header">CIERRE</td>
        </tr>
        <tr v-if="cierreReflexion">
          <td class="content-cell cierre">
            <div class="subsection-title">Reflexión final</div>
            <p v-html="cierreReflexion"></p>
          </td>
        </tr>
        <tr v-if="cierreDespedida">
          <td class="content-cell cierre">
            <div class="subsection-title">Despedida</div>
            <p v-html="cierreDespedida"></p>
          </td>
        </tr>
      </table>
      <!-- La burbuja de acuarela de materiales para la clase. Se disuelve en
           cuanto llega el primer trozo del stream de GPT, que es justo cuando
           aparece el primer texto en la tabla. `hold-ms=0` = no se apaga sola:
           si la generacion demora, sigue ahi.
           OJO: el loader es `position:absolute; inset:0`, o sea un overlay.
           Necesita un padre con posicion y altura propias (como el .viewport de
           MaterialStreamCards) o colapsa a cero y no se ve. -->
      <div v-if="!done && loaderVisible" class="plani-loader-stage">
        <MaterialStreamLoader
          ref="streamLoader"
          :messages="loaderMessages"
          morph-message="La idea se convierte en acuarela…"
          open-message="Abriendo tu planificación…"
          :hold-ms="0"
        />
      </div>
    </div>
  </div>
</template>

<script>
import chatBotService from "@/services/chatBotService";
import MaterialStreamLoader from "./MaterialStreamLoader.vue";
import { inlineMarkdown } from "@/utils/inlineMarkdown";
import { followContentBottom, watchUserScroll } from "@/utils/scrollToTop";

// Orden en que el modelo entrega las secciones de la planificacion. Sirve para
// saber cuando una seccion quedo cerrada: si ya llego una posterior, la
// anterior esta completa.
const ORDEN_SECCIONES = [
  "question",
  "question_relation",
  "introduction",
  "explanation",
  "steps",
  "errors",
  "concepts",
  "worked_example",
  "remember",
  "understand",
  "apply",
  "analyze",
  "evaluate",
  "final_thoughts",
  "motivation",
];

export default {
  name: "DailyPlanificationModified",
  components: {
    MaterialStreamLoader
  },
  props: {
    subject: { type: String, required: false },
    grade: { type: String, required: false },
    oa: { type: Object, required: false },
    ind: { type: Array, required: false },
    type: { type: String, required: false },
    demo: { type: Boolean, required: false },
    // Demo mode: raw pre-recorded response of get_daily_planification_structured,
    // replayed locally in chunks so the table "streams" without any AI call.
    demoStreamText: { type: String, required: false, default: null },
    condition: { type: String, required: true },
  },
  data() {
    return {
      text: "", // Raw text from the API response
      // New data structure based on second_try.py
      planificationData: null,
      
      // Computed properties for display
      inicioPregunta: "",
      inicioBajada: "",
      inicioIntro: "",
      desarrolloConceptos: [],
      desarrolloExplicacion: [],
      desarrolloEjemplo: null,
      desarrolloPasoAPaso: [],
      desarrolloErrores: [],
      ejerciciosBloom: [],
      cierreReflexion: "",
      cierreDespedida: "",
      done: false,
      demoReplayTimer: null,
      // Se apaga cuando la burbuja termino su disolucion, para no dejar el
      // hueco del contenedor vacio debajo de la tabla.
      loaderVisible: true,
      loaderReleased: false,
      // Estado del seguimiento del scroll mientras crece la tabla.
      scrollEstado: { usuarioSeFue: false, pendiente: false },
      // Secciones cuyo cierre ya se aviso, para no repetir el aviso.
      seccionesAvisadas: {},
      dejarDeVigilarScroll: null,
      loaderMessages: [
        [
          "Armando tu <em>planificación</em>",
          "Leyendo el objetivo de aprendizaje y sus indicadores…",
        ],
        [
          "Mezclando educación con <em>amor</em>",
          "Cuidando el inicio, el desarrollo y el cierre de tu clase…",
        ],
      ]
    };
  },
  computed: {
    planTitle() {
      let title = "";
      if (this.oa && this.oa.name) {
        title = this.oa.name;
      } else if (this.oa && typeof this.oa === "string") {
        title = this.oa;
      } else if (this.oa && this.oa.title) {
        title = this.oa.title;
      } else {
        title = "Planificación diaria";
      }

      // Clean HTML from title
      title = title.replace(/<[^>]*>/g, "");
      return title;
    },
    planIndicator() {
      if (this.planificationData && this.planificationData.state && this.planificationData.state.INDICADOR) {
        return this.planificationData.state.INDICADOR;
      }
      
      if (this.ind && this.ind.length > 0) {
        const indicators = this.ind
          .map((i) => {
            let name = i.name || "";
            name = name.replace(/<[^>]*>/g, "");
            return name;
          })
          .join(", ");
        return indicators;
      }
      return "";
    },
  },
  mounted() {
    this.dejarDeVigilarScroll = watchUserScroll(this.scrollEstado);
    if (this.demo && this.demoStreamText) {
      this.replayDemoStream();
    } else if (!this.demo) {
      this.getDailyPlanificationStructured();
    }
  },
  beforeDestroy() {
    if (this.demoReplayTimer) clearInterval(this.demoReplayTimer);
    if (this.dejarDeVigilarScroll) this.dejarDeVigilarScroll();
  },
  methods: {
    /**
     * Suelta la burbuja de acuarela: arranca su disolucion y la desmonta
     * cuando termina la animacion. Idempotente, porque el stream entrega
     * muchos trozos y solo el primero debe disparar la salida.
     */
    releaseLoader() {
      if (this.loaderReleased) return;
      this.loaderReleased = true;
      const loader = this.$refs.streamLoader;
      if (!loader || !loader.releaseBurst) {
        this.loaderVisible = false;
        return;
      }
      loader.releaseBurst();
      if (loader.whenDone) {
        loader.whenDone().then(() => {
          this.loaderVisible = false;
        });
      } else {
        this.loaderVisible = false;
      }
    },
    // Feed the pre-recorded stream through the exact same accumulate-and-parse
    // path as the real fetch (see getDailyPlanificationStructured), so the demo
    // render is indistinguishable from a live generation.
    replayDemoStream() {
      const full = this.demoStreamText;
      const CHUNK_SIZE = 15;
      let cursor = 0;
      this.demoReplayTimer = setInterval(() => {
        // Mismo contrato que el stream real: el primer trozo suelta la burbuja.
        this.releaseLoader();
        this.text += full.slice(cursor, cursor + CHUNK_SIZE);
        cursor += CHUNK_SIZE;
        if (cursor >= full.length) {
          clearInterval(this.demoReplayTimer);
          this.demoReplayTimer = null;
          this.parseStructuredData(true);
          this.done = true;
        } else {
          this.parseStructuredData();
        }
      }, 25);
    },
    formatBuffer(buffer) {
      const pendingElements = [];
      let newBuffer = buffer;
      for (let i = 0; i < buffer.length; i++) {
        if (buffer[i] === "{") {
          pendingElements.push("{");
        }
        if (buffer[i] === "[") {
          pendingElements.push("[");
        }
        if (buffer[i] === '"') {
          if (
            pendingElements.length > 0 &&
            pendingElements[pendingElements.length - 1] === '"'
          ) {
            pendingElements.pop();
          } else {
            pendingElements.push('"');
          }
        }
        if (buffer[i] === "}") {
          if (
            pendingElements.length > 0 &&
            pendingElements[pendingElements.length - 1] === "{"
          ) {
            pendingElements.pop();
          }
        }
        if (buffer[i] === "]") {
          if (
            pendingElements.length > 0 &&
            pendingElements[pendingElements.length - 1] === "["
          ) {
            pendingElements.pop();
          }
        }
      }
      while (pendingElements.length > 0) {
        const lastElement = pendingElements.pop();
        if (lastElement === "{") {
          newBuffer += "}";
        } else if (lastElement === '"') {
          newBuffer += '"';
        } else if (lastElement === "[") {
          newBuffer += "]";
        }
      }
      return newBuffer;
    },
    getDailyPlanificationStructured() {
      var clearOA = this.oa;
      clearOA.subtags = [];
      chatBotService
        .getDailyPlanificationStructured(
          this.subject,
          this.grade,
          clearOA,
          this.type,
          this.ind.map((i) => {
            return { name: i.name, description: i.description };
          }),
          this.condition
        )
        .then((body) => {
          const reader = body.getReader();
          const decoder = new TextDecoder();
          var vue_context = this;
          
          function processChunk({ done, value }) {
            if (value) {
              const decodedChunk = decoder.decode(value);
              // Primer trozo de GPT = ya hay algo que mostrar. Aca arranca la
              // disolucion de la burbuja, para que se apague justo cuando la
              // tabla empieza a llenarse y no antes ni despues.
              vue_context.releaseLoader();
              vue_context.text += decodedChunk;
              // console.log(vue_context.text)
              vue_context.parseStructuredData();
            }
            if (done) {
              vue_context.parseStructuredData(done);
              vue_context.done = true;
              return;
            }
            return reader.read().then(processChunk);
          }
          reader.read().then(processChunk);
        });
    },
    parseStructuredData(done) {
      try {
        // Try to parse the |received data as JSON
        const data = JSON.parse(this.text);
        
        if (data && data.question) {
          this.planificationData = data;
          this.updateDisplayData();
          if (done) {
            this.avisarSeccionesListas(true);
            this.$emit('done', this.planificationData);
          }
        }
      } catch (e) {
        try {
            const partial = this.formatBuffer(this.text);
            const data = JSON.parse(partial);
            if (data && data.question) {
                this.planificationData = data;
                this.updateDisplayData();
                if (done) {
                  this.avisarSeccionesListas(true);
                  this.$emit('done', this.planificationData);
                }
            }
        } catch(er) {
            null;
        }
      }
    },
    
    /**
     * Avisa al padre cada vez que una seccion queda CERRADA, para que pueda ir
     * generando las slides que dependen de ella sin esperar al resto.
     *
     * Como se sabe que una seccion esta completa: el modelo las entrega en
     * orden, asi que si ya aparecio una posterior, la anterior termino. Mirar
     * la seccion en si no sirve, porque el parseo parcial repara el JSON y una
     * seccion a medio llegar parece entera.
     */
    avisarSeccionesListas(todoListo) {
      const datos = this.planificationData || {};
      let ultimaVista = -1;
      ORDEN_SECCIONES.forEach((nombre, i) => {
        if (datos[nombre]) ultimaVista = i;
      });
      // Con el stream terminado, la ultima tambien esta cerrada.
      const hasta = todoListo ? ultimaVista + 1 : ultimaVista;
      for (let i = 0; i < hasta; i++) {
        const nombre = ORDEN_SECCIONES[i];
        if (this.seccionesAvisadas[nombre] || !datos[nombre]) continue;
        this.seccionesAvisadas[nombre] = true;
        this.$emit("seccion-lista", { seccion: nombre, planificacion: datos });
      }
    },
    updateDisplayData() {
      // Cada vez que entra contenido nuevo, acompanar el final del bloque para
      // que se vea construirse hacia abajo (sin robarle el scroll al que sube
      // a releer: de eso se encarga followContentBottom).
      this.$nextTick(() =>
        followContentBottom(this.$el, this.scrollEstado)
      );
      this.avisarSeccionesListas(false);

      const results = this.planificationData;
      
      // INICIO section
      if (results.question) {
        this.inicioPregunta = this.formatRichText(results.question.question);
      }
      
      if (results.question_relation) {
        this.inicioBajada = this.formatRichText(results.question_relation.relation_paragraph);
      }
      
      if (results.introduction) {
        this.inicioIntro = this.formatRichText(results.introduction.intro_text);
      }
      
      // DESARROLLO section
      if (results.explanation) {
        this.desarrolloExplicacion = results.explanation.sections || [];
      }
      
      if (results.steps) {
        this.desarrolloPasoAPaso = (results.steps.steps || []).map(step => 
          this.formatRichText(step.title + ": " + step.description)
        );
      }

      if (results.errors) {
        this.desarrolloErrores = results.errors.errors || [];
      }
      
      if (results.concepts) {
        this.desarrolloConceptos = results.concepts.concepts || [];
      }
      
      if (results.worked_example) {
        this.desarrolloEjemplo = results.worked_example;
      }
      
      // BLOOM exercises
      this.ejerciciosBloom = this.buildBloomExercises(results);
      
      // CIERRE section
      if (results.final_thoughts) {
        this.cierreReflexion = this.formatRichText(results.final_thoughts.final_thoughts);
      }
      
      if (results.motivation) {
        this.cierreDespedida = this.formatRichText(results.motivation.closing_message);
      }
    },
    
    buildBloomExercises(results) {
      const exercises = [];
      const bloomLevels = [
        { key: 'remember', title: 'Nivel 1 Recordar datos o conceptos', 'name': 'Recordar' },
        { key: 'understand', title: 'Nivel 2 Explicar con sus propias palabras', 'name': 'Entender' },
        { key: 'apply', title: 'Nivel 3 Aplicar en situaciones nuevas', 'name': 'Aplicar' },
        { key: 'analyze', title: 'Nivel 4 Analizar la información', 'name': 'Analizar' },
        { key: 'evaluate', title: 'Nivel 5 Evaluar contextos', 'name': 'Evaluar' }
      ];
      
      bloomLevels.forEach(level => {
        const levelData = results[level.key];
        if (levelData) {
          let pregunta = levelData.question || '';
          let opciones = '';
          let respuesta = '';
          
          if (levelData.alternatives) {
            // El `<br>` lo pone este codigo, no el modelo: hay que escapar cada
            // alternativa por separado y recien despues unirlas. Si se escapa la
            // cadena ya unida (como pasaba al empezar a escapar en
            // formatRichText), los separadores salen como "&lt;br&gt;" y las
            // alternativas quedan todas en una linea.
            opciones = levelData.alternatives
              .map((alt) => this.formatRichText(`${alt.key}) ${alt.text}`))
              .join('<br>');
          }
          
          if (levelData.answer_key) {
            respuesta = `Respuesta correcta: ${levelData.answer_key}`;
          } else if (levelData.rubric) {
            // Build rubric table
            respuesta = this.buildRubricTable(levelData.rubric);
          }
          
          exercises.push({
            nivel: level.name.charAt(0).toUpperCase() + level.name.slice(1),
            titulo: level.title,
            pregunta: this.formatRichText(pregunta),
            // `opciones` ya viene escapada alternativa por alternativa.
            opciones: opciones,
            escenario: this.formatRichText(levelData.scenario || ''),
            respuesta: respuesta
          });
        }
      });
      
      return exercises;
    },
    
    buildRubricTable(rubric) {
      if (!rubric || !rubric.levels || !Array.isArray(rubric.levels)) {
        return '';
      }
      
      let tableHtml = '<div class="rubric-container d-lg-block d-none"><h5>Rúbrica de evaluación:</h5><table class="rubric-table">';
      
      // Add table header
      tableHtml += '<thead><tr>';
      tableHtml += '<th>Nivel</th>';
      tableHtml += '<th>Criterio</th>';
      tableHtml += '<th>Ejemplo</th>';
      tableHtml += '</tr></thead>';
      
      // Add table body
      tableHtml += '<tbody>';
      rubric.levels.forEach(level => {
        tableHtml += '<tr>';
        tableHtml += `<td class="level-cell"><strong>${level.level || 'Nivel'}</strong></td>`;
        tableHtml += `<td class="criteria-cell">${level.criteria || ''}</td>`;
        tableHtml += `<td class="example-cell"><em>${level.example || ''}</em></td>`;
        tableHtml += '</tr>';
      });
      tableHtml += '</tbody></table></div>';
      
      return tableHtml;
    },
    // Rich text formatting
    formatRichText(text) {
      if (!text) return "";

      // El texto viene de un modelo y se pinta con v-html, asi que se escapa
      // antes de convertir nada: `inlineMarkdown` hace escape -> negrita ->
      // cursiva, en ese orden. Antes la negrita se convertia sobre el texto
      // crudo, lo que dejaba pasar cualquier HTML de la respuesta.
      const normalizado = String(text).trim().replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      return inlineMarkdown(normalizado).replace(/\n/g, "<br>");
    },
    
    // Methods for downloads (keeping the same structure)
    buildSectionsObject() {
      // This method can be adapted to work with the new data structure
      // For now, returning the original structure for compatibility
      return {
        sections: [],
        oa: this.oa || {},
        ind: this.ind || [],
      };
    },
    
    downloadWord() {
      const planification = this.buildSectionsObject();
      import("@/services/chatBotService").then((module) => {
        module.default
          .downloadPlanification({ planification })
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Planificacion_Diaria.docx");
            document.body.appendChild(link);
            link.click();
            link.remove();
          });
      });
    },
  },
};
</script>

<style scoped>
/* ---- Paleta ----
   Los tres colores de seccion salen de los pigmentos de la burbuja de acuarela
   (MaterialStreamLoader: .perfect-teal / .perfect-pink / .perfect-amber). Antes
   eran #20b2aa, #c6588d y #f8ac04, elegidos por separado y de familias
   distintas: puestos al lado de la burbuja no combinaban con nada. Ahora los
   dos momentos del producto usan los mismos pigmentos. */
.lesson-container {
  --acu-teal: #4ecdc4;
  --acu-teal-claro: #78e2d8;
  --acu-teal-texto: #14706a;

  --acu-pink: #ff6b9d;
  --acu-pink-claro: #ff96be;
  --acu-pink-texto: #a32e5c;

  --acu-amber: #ffc73c;
  --acu-amber-claro: #ffde78;
  --acu-amber-texto: #8a5b00;

  font-family: "Ubuntu", -apple-system, BlinkMacSystemFont, "Segoe UI",
    system-ui, sans-serif;
  background: #f7f7f7;
  min-height: 100vh;
  padding: 10px;
  position: relative;
}

.lesson-container .container {
  /* `.container` es de Bootstrap y trae 15px de padding horizontal. Eso metia
     las barras de color 15px hacia adentro, dejando asomar una franja blanca
     entre el borde redondeado de la tarjeta y el bloque de color: el "mal
     encaje". Las celdas ya traen su propio padding (14px 20px), asi que el
     texto no queda pegado al borde. */
  padding-left: 0;
  padding-right: 0;
  max-width: 1000px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08), 0 5px 15px rgba(0, 0, 0, 0.04);
  position: relative;
  isolation: isolate;
}

.lesson-container .header {
  background: linear-gradient(
    135deg,
    var(--acu-teal) 0%,
    var(--acu-teal-claro) 100%
  );
  color: white;
  padding: 25px 30px;
  position: relative;
  z-index: 10;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* El encaje: este bloque llevaba su propio border-radius ademas del que ya
     aplica `.container` (que ademas recorta con overflow:hidden). Dos radios
     encimados dejaban asomar una franja de color entre el borde de la tarjeta y
     el blanco del contenedor. El recorte lo hace el padre y basta. */
  overflow: hidden;
  /* La sombra teal marcada agrandaba visualmente ese desajuste; se deja una
     separacion sobria. */
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
  /* El nombre del OA y los indicadores son de largo variable: el bloque tiene
     que crecer con ellos y nunca recortarlos. `overflow: hidden` esta solo para
     contener los degradados decorativos del ::before. */
  height: auto;
}

.lesson-container .header h1,
.lesson-container .header p {
  overflow-wrap: anywhere;
}

.lesson-container .header::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.15) 100%
  );
  z-index: 11;
  backdrop-filter: blur(10px);
}

.lesson-container .header::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle at 80% 20%,
    rgba(255, 255, 255, 0.2) 0%,
    transparent 50%
  );
  animation: headerShimmer 4s ease-in-out infinite;
  z-index: 1;
}

@keyframes headerShimmer {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: rotate(180deg) scale(1.1);
    opacity: 0.1;
  }
}

.lesson-container .header h1 {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 10px;
  position: relative;
  z-index: 12;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 255, 255, 0.1);
  line-height: 1.5;
  word-wrap: break-word;
  hyphens: auto;
  /* Los 80px que se descontaban aca eran el hueco de la pelotita decorativa,
     que ya no existe. Sin ese descuento el nombre del OA usa todo el ancho y
     necesita menos lineas. */
}

.lesson-container .header p {
  font-size: 1rem;
  opacity: 0.95;
  position: relative;
  z-index: 12;
  font-weight: 400;
  line-height: 1.5;
  word-wrap: break-word;
  margin: 0;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
}

.lesson-container .floating-orb {
  position: absolute;
  top: 15px;
  right: 25px;
  width: 60px;
  height: 60px;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.3)
  );
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(15px);
  z-index: 15;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.4),
    0 0 40px rgba(78, 205, 196, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.3);
  animation: orbMagic 6s ease-in-out infinite;
}

.lesson-container .floating-orb::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.8) 0%,
    transparent 70%
  );
  border-radius: 50%;
  animation: orbCore 3s ease-in-out infinite alternate;
}

.lesson-container .floating-orb::after {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  animation: orbRing 4s linear infinite;
}

@keyframes orbMagic {
  0%,
  100% {
    transform: translateY(0px) scale(1) rotate(0deg);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.4),
      0 0 40px rgba(78, 205, 196, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.3);
  }
  33% {
    transform: translateY(-8px) scale(1.1) rotate(120deg);
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.6),
      0 0 60px rgba(78, 205, 196, 0.5), inset 0 0 25px rgba(255, 255, 255, 0.4);
  }
  66% {
    transform: translateY(-4px) scale(0.95) rotate(240deg);
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.5),
      0 0 50px rgba(132, 206, 199, 0.4),
      inset 0 0 22px rgba(255, 255, 255, 0.35);
  }
}

@keyframes orbCore {
  0% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

@keyframes orbRing {
  0% {
    transform: rotate(0deg) scale(1);
    opacity: 0.3;
  }
  50% {
    opacity: 0.1;
  }
  100% {
    transform: rotate(360deg) scale(1.1);
    opacity: 0.3;
  }
}

.lesson-container .planning-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: transparent;
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 1;
}

.lesson-container .section-header {
  color: white;
  font-weight: 700;
  text-align: center;
  padding: 12px 15px;
  font-size: 0.9rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3), 0 0 15px rgba(255, 255, 255, 0.1);
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: none;
}

.lesson-container .planning-table tr:first-child td {
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
}

.lesson-container .planning-table tr:last-child td {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.lesson-container .inicio-header {
  background: linear-gradient(135deg, var(--acu-teal) 0%, var(--acu-teal-claro) 100%);
}

.lesson-container .desarrollo-header {
  background: linear-gradient(135deg, var(--acu-pink) 0%, var(--acu-pink-claro) 100%);
}

.lesson-container .cierre-header {
  background: linear-gradient(135deg, var(--acu-amber) 0%, var(--acu-amber-claro) 100%);
}

.lesson-container .section-header::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.7),
    transparent
  );
  border-radius: 2px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.lesson-container .content-cell {
  padding: 14px 20px;
  background: #ffffff;
  color: #2d3748;
  line-height: 1.4;
  font-size: 0.85rem;
  border-left: 4px solid transparent;
  border-bottom: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
}

.lesson-container .content-cell.inicio {
  border-left: 4px solid var(--acu-teal);
}

.lesson-container .content-cell.desarrollo {
  border-left: 4px solid var(--acu-pink);
}

.lesson-container .content-cell.cierre {
  border-left: 4px solid var(--acu-amber);
}

.lesson-container .content-cell:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  z-index: 3;
  border-radius: 8px;
}

.lesson-container .subsection-title {
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-block;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.lesson-container .content-cell.inicio .subsection-title {
  background: linear-gradient(
    135deg,
    rgba(78, 205, 196, 0.18),
    rgba(120, 226, 216, 0.1)
  );
  color: var(--acu-teal-texto);
  border-left: 3px solid var(--acu-teal);
  box-shadow: 0 2px 8px rgba(78, 205, 196, 0.18);
}

.lesson-container .content-cell.desarrollo .subsection-title {
  background: linear-gradient(
    135deg,
    rgba(255, 107, 157, 0.18),
    rgba(255, 150, 190, 0.1)
  );
  color: var(--acu-pink-texto);
  border-left: 3px solid var(--acu-pink);
  box-shadow: 0 2px 8px rgba(255, 107, 157, 0.18);
}

.lesson-container .content-cell.cierre .subsection-title {
  background: linear-gradient(
    135deg,
    rgba(255, 199, 60, 0.22),
    rgba(255, 222, 120, 0.12)
  );
  color: var(--acu-amber-texto);
  border-left: 3px solid var(--acu-amber);
  box-shadow: 0 2px 8px rgba(255, 199, 60, 0.2);
}

.lesson-container .content-cell p {
  margin-bottom: 6px;
  color: #374151;
  font-weight: 400;
  line-height: 1.5;
}

.lesson-container .content-cell p:last-child {
  margin-bottom: 0;
}

/* New styles for structured data */
.explanation-section {
  margin-bottom: 20px;
  padding: 15px;
  background: rgba(255, 107, 157, 0.05);
  border-radius: 8px;
  border-left: 4px solid var(--acu-pink);
}

.explanation-section h4 {
  color: var(--acu-pink-texto);
  margin-bottom: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.error-card {
  background: rgba(255, 107, 157, 0.05);
  border: 1px solid rgba(255, 107, 157, 0.2);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  border-left: 4px solid var(--acu-pink);
  box-shadow: 0 2px 8px rgba(255, 107, 157, 0.1);
}

.error-card h4 {
  color: var(--acu-pink-texto);
  margin-bottom: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.concept-card {
  background: rgba(255, 107, 157, 0.05);
  border: 1px solid rgba(255, 107, 157, 0.2);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  border-left: 4px solid var(--acu-pink);
  box-shadow: 0 2px 8px rgba(255, 107, 157, 0.1);
}

.concept-card h4 {
  color: #7a2d5c;
  margin-bottom: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.concept-card h4.bold-term {
  background: linear-gradient(135deg, #ff6b9d, #ff96be);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  margin: -15px -15px 15px -15px;
}

.worked-example-card {
  background: rgba(255, 107, 157, 0.05);
  border: 1px solid rgba(255, 107, 157, 0.2);
  border-radius: 8px;
  padding: 15px;
  border-left: 4px solid var(--acu-pink);
  box-shadow: 0 2px 8px rgba(255, 107, 157, 0.1);
}

.worked-example-card h4 {
  color: var(--acu-pink-texto);
  margin-bottom: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.worked-example-card h5 {
  color: #7a2d5c;
  margin: 15px 0 8px 0;
  font-size: 0.8rem;
  font-weight: 600;
}

.lesson-container .exercise-card {
  background: linear-gradient(
    135deg,
    rgba(255, 107, 157, 0.08),
    rgba(232, 181, 209, 0.05)
  );
  border: 1px solid rgba(255, 107, 157, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  margin: 8px 0;
  border-left: 4px solid var(--acu-pink);
  font-size: 0.8rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(255, 107, 157, 0.1);
  z-index: 1;
}

.lesson-container .exercise-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 107, 157, 0.08));
  z-index: 0;
}

.lesson-container .exercise-card:hover {
  transform: translateY(-2px) translateX(2px);
  box-shadow: 0 6px 20px rgba(255, 107, 157, 0.15);
  border-color: var(--acu-pink-texto);
  background: linear-gradient(
    135deg,
    rgba(255, 107, 157, 0.12),
    rgba(232, 181, 209, 0.08)
  );
}

.lesson-container .exercise-level {
  font-weight: 700;
  color: var(--acu-pink-texto);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  background: rgba(255, 107, 157, 0.15);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  position: relative;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(255, 107, 157, 0.2);
}

.lesson-container .exercise-content {
  position: relative;
  z-index: 2;
}

.lesson-container .exercise-content p {
  margin-bottom: 6px;
  line-height: 1.5;
}

.lesson-container .exercise-options {
  font-size: 0.75rem;
  margin: 6px 0;
  color: #555;
  padding-left: 10px;
}

.lesson-container .exercise-answer {
  font-weight: 600;
  color: var(--acu-pink-texto);
  font-size: 0.75rem;
  padding: 4px 8px;
  background: rgba(255, 107, 157, 0.1);
  border-radius: 4px;
  margin-top: 4px;
}

/* Rubric table styles */
.rubric-container {
  width: 100%;
  overflow-x: scroll;
  margin-top: 8px;
}

.rubric-container h5 {
  color: var(--acu-pink-texto);
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.rubric-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(255, 107, 157, 0.1);
  border: 1px solid rgba(255, 107, 157, 0.2);
}

.rubric-table th {
  background: linear-gradient(135deg, #ff6b9d, #ff96be);
  color: white;
  font-weight: 600;
  padding: 8px 6px;
  text-align: center;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rubric-table td {
  padding: 8px 6px;
  border-bottom: 1px solid rgba(255, 107, 157, 0.1);
  vertical-align: top;
  line-height: 1.3;
}

.rubric-table tbody tr:hover {
  background: rgba(255, 107, 157, 0.05);
}

.rubric-table .level-cell {
  font-weight: 600;
  color: #7a2d5c;
  background: rgba(255, 107, 157, 0.05);
  border-right: 1px solid rgba(255, 107, 157, 0.2);
  width: 20%;
  text-align: center;
}

.rubric-table .criteria-cell {
  width: 50%;
  border-right: 1px solid rgba(255, 107, 157, 0.1);
}

.rubric-table .example-cell {
  width: 30%;
  color: #666;
  font-style: italic;
}

.lesson-container .content-cell ul {
  list-style: none;
  padding-left: 0;
  margin: 6px 0;
}

.lesson-container .content-cell li {
  position: relative;
  padding: 4px 0 4px 20px;
  margin-bottom: 3px;
  font-size: 0.8rem;
  color: #374151;
  line-height: 1.4;
}

.lesson-container .content-cell.inicio li::before {
  content: "●";
  position: absolute;
  left: 8px;
  top: 4px;
  font-size: 0.8rem;
  color: var(--acu-teal-texto);
  text-shadow: 0 0 8px rgba(78, 205, 196, 0.4);
}

.lesson-container .content-cell.desarrollo li::before {
  content: "●";
  position: absolute;
  left: 8px;
  top: 4px;
  font-size: 0.8rem;
  color: var(--acu-pink-texto);
  text-shadow: 0 0 8px rgba(255, 107, 157, 0.4);
}

.lesson-container .content-cell.cierre li::before {
  content: "●";
  position: absolute;
  left: 8px;
  top: 4px;
  font-size: 0.8rem;
  color: var(--acu-amber-texto);
  text-shadow: 0 0 8px rgba(255, 199, 60, 0.4);
}

@media (max-width: 768px) {
  .lesson-container {
    padding: 5px;
  }

  .lesson-container .container {
    border-radius: 8px;
  }

  .lesson-container .header {
    padding: 15px 20px;
  }

  .lesson-container .header h1 {
    font-size: 1.2rem;
  }

  .lesson-container .content-cell {
    padding: 10px 15px;
    font-size: 0.8rem;
  }

  .lesson-container .section-header {
    padding: 8px 12px;
    font-size: 0.8rem;
  }

  .lesson-container .floating-orb {
    width: 50px;
    height: 50px;
    top: 12px;
    right: 18px;
  }
}

@media (max-width: 480px) {
  .lesson-container .content-cell {
    font-size: 0.75rem;
  }

  .lesson-container .exercise-card {
    font-size: 0.75rem;
  }

  .lesson-container .subsection-title {
    font-size: 0.7rem;
  }
}
</style>

<!-- Estilos globales para contenido dinámico (v-html) -->
<style>
/* Variables CSS globales */
/* Los nombres se mantienen (los usa tambien DailyPlanification) pero los
   valores pasan a ser los pigmentos de la burbuja de acuarela, para que la
   tabla y el resto del producto hablen el mismo idioma de color. */
:root {
  --naranjo-fuerte: #ffc73c;
  --naranjo-suave: #ffde78;
  --verde-fuerte: #4ecdc4;
  --verde-suave: #78e2d8;
  --morado-fuerte: #ff6b9d;
  --morado-suave: #ff96be;
}

/* Estilos para elementos strong dentro del contenido v-html */
.lesson-container .content-cell strong {
  color: #ffffff !important;
  padding: 3px 8px !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 0.8em !important;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  margin: 1px 2px !important;
  position: relative !important;
  z-index: 1 !important;
}

.lesson-container .content-cell.inicio strong {
  background: linear-gradient(
    135deg,
    var(--verde-fuerte),
    var(--verde-suave)
  ) !important;
  box-shadow: 0 2px 8px rgba(78, 205, 196, 0.4) !important;
}

.lesson-container .content-cell.desarrollo strong {
  background: linear-gradient(
    135deg,
    var(--morado-fuerte),
    var(--morado-suave)
  ) !important;
  box-shadow: 0 2px 8px rgba(255, 107, 157, 0.4) !important;
}

.lesson-container .content-cell.cierre strong {
  background: linear-gradient(
    135deg,
    var(--naranjo-fuerte),
    var(--naranjo-suave)
  ) !important;
  box-shadow: 0 2px 8px rgba(255, 199, 60, 0.4) !important;
}

.lesson-container .content-cell strong:hover {
  transform: translateY(-1px) scale(1.05) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  z-index: 2 !important;
}

/* Estilos para listas dentro del contenido v-html */
.lesson-container .content-cell ul {
  list-style: none !important;
  padding-left: 0 !important;
  margin: 6px 0 !important;
  position: relative !important;
}

.lesson-container .content-cell li {
  position: relative !important;
  padding: 4px 0 4px 20px !important;
  margin-bottom: 3px !important;
  font-size: 0.8rem !important;
  color: #374151 !important;
  line-height: 1.4 !important;
  transition: color 0.2s ease !important;
}

.lesson-container .content-cell li:hover {
  color: #1f2937 !important;
}

.lesson-container .content-cell.inicio li::before {
  content: "●" !important;
  position: absolute !important;
  left: 8px !important;
  top: 4px !important;
  font-size: 0.8rem !important;
  color: var(--verde-fuerte) !important;
  text-shadow: 0 0 8px rgba(78, 205, 196, 0.4) !important;
  transition: all 0.2s ease !important;
}

.lesson-container .content-cell.desarrollo li::before {
  content: "●" !important;
  position: absolute !important;
  left: 8px !important;
  top: 4px !important;
  font-size: 0.8rem !important;
  color: var(--morado-fuerte) !important;
  text-shadow: 0 0 8px rgba(255, 107, 157, 0.4) !important;
  transition: all 0.2s ease !important;
}

.lesson-container .content-cell.cierre li::before {
  content: "●" !important;
  position: absolute !important;
  left: 8px !important;
  top: 4px !important;
  font-size: 0.8rem !important;
  color: var(--naranjo-fuerte) !important;
  text-shadow: 0 0 8px rgba(255, 199, 60, 0.4) !important;
  transition: all 0.2s ease !important;
}

.lesson-container .content-cell li:hover::before {
  transform: scale(1.2) !important;
  text-shadow: 0 0 12px rgba(255, 255, 255, 0.6) !important;
}

/* Estilos para párrafos dentro del contenido v-html */
.lesson-container .content-cell p {
  margin-bottom: 6px !important;
  color: #374151 !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
  transition: color 0.2s ease !important;
}

.lesson-container .content-cell p:last-child {
  margin-bottom: 0 !important;
}

/* Optimización para evitar conflictos de z-index */
.lesson-container .content-cell:hover .subsection-title {
  z-index: 1 !important;
}

.lesson-container .content-cell:hover .exercise-card {
  z-index: 1 !important;
}

/* Asegurar que los elementos de la tabla no interfieran con el header */
.lesson-container .planning-table {
  position: relative !important;
  z-index: 1 !important;
}

/* Escenario para la burbuja de acuarela: el loader se posiciona en absoluto
   sobre este contenedor, asi que aca vive su altura. */
.plani-loader-stage {
  position: relative;
  min-height: 320px;
  margin: 8px 0 4px;
  border-radius: 28px;
  overflow: hidden;
}

@media (max-width: 576px) {
  .plani-loader-stage {
    min-height: 260px;
  }
}
</style>
